<template>
  <div class="dashboard-page mt-3">
    <CCard class="dashboard-card border-0 shadow-sm mt-3">
      <CCardBody>
        <CAlert v-if="errorMessage" color="danger" show>
          {{ errorMessage }}
        </CAlert>

        <div class="filter-bar mb-3">
          <DateRangePicker
            v-model="filters"
            :disabled="isLoading"
            @preset="applyFilters"
          />
          <div class="filter-actions">
            <CButton
              color="primary"
              class="action-btn"
              :disabled="isLoading"
              @click="applyFilters"
            >
              {{ isLoading ? 'Loading...' : 'Search' }}
            </CButton>
            <CButton
              color="dark"
              variant="outline"
              class="action-btn reload-icon-btn"
              :disabled="isLoading"
              title="Reload"
              aria-label="Reload"
              @click="reloadData"
            >
              <span class="reload-glyph" aria-hidden="true">&#x21bb;</span>
            </CButton>
          </div>
        </div>

        <CRow class="tight-row tiles-row pt-3">
          <template v-if="isLoading && !tileCards.length">
            <CCol
              v-for="i in skeletonTileCount"
              :key="`sk-${i}`"
              xl="3"
              lg="4"
              sm="6"
              class="mb-2"
              style="height: 95px !important"
            >
              <div class="tile-skeleton" aria-hidden="true">
                <div class="tile-skeleton__body">
                  <div class="tile-skeleton__lines">
                    <div class="tile-skeleton__line tile-skeleton__line--value"></div>
                    <div class="tile-skeleton__line tile-skeleton__line--label"></div>
                  </div>
                </div>
              </div>
            </CCol>
          </template>
          <CCol
            v-for="(card, index) in tileCards"
            :key="card.key"
            xl="3"
            lg="4"
            sm="6"
            class="mb-2"
            style="height: 95px !important"
          >
            <CCard :class="['h-100 border-0 tile-card', tileCardClass(card.key, index)]">
              <CCardBody class="tile-body">
                <div class="tile-text">
                  <div class="tile-value">{{ formatTileValue(card.key, card.value) }}</div>
                  <div class="tile-label">{{ card.label }}</div>
                </div>
                <span class="tile-icon" aria-hidden="true">
                  <CIcon :name="tileIcon(card.key)" size="lg" />
                </span>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <div class="reports-section pt-3">
          <div class="report-tabs" role="tablist">
            <button
              v-for="tab in reportTabs"
              :key="tab.key"
              type="button"
              role="tab"
              class="report-tab"
              :class="{ 'is-active': tab.key === activeReport }"
              :aria-selected="tab.key === activeReport"
              @click="activeReport = tab.key"
            >
              <span class="tab-label">{{ tab.label }}</span>
              <span v-if="!isLoading && tab.items.length" class="tab-count">{{ tab.items.length }}</span>
            </button>
          </div>

          <BaseTable
            :accent="activeTab.accent"
            :items="activeTab.items"
            :loading="isLoading"
            :empty-text="activeTab.emptyText"
            :show-count="false"
          />
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import axios from 'axios'
import { tileIcon as tileIconFor } from '@/config/dashboardTileIcons'

const TILE_LABELS = {
  no_of_orders_today: 'My Orders',
  bottles_sold_today: 'Bottles Sold by Me',
  sales_amount: 'My Sales Amount',
  pending_payment: 'My Pending Payment',
  return_rate: 'My Return Rate',
  returns: 'My Returns'
}

const TILE_ORDER = [
  'no_of_orders_today',
  'bottles_sold_today',
  'sales_amount',
  'pending_payment',
  'return_rate',
  'returns'
]

export default {
  name: 'FieldSalesDashboard',
  data () {
    const today = this.toDateInputValue(new Date())
    return {
      isLoading: false,
      errorMessage: '',
      lastUpdatedAt: null,
      filters: {
        fromDate: today,
        toDate: today
      },
      tiles: {},
      dailyBreakdown: [],
      itemwiseReport: [],
      recentOrders: [],
      activeReport: 'daily'
    }
  },
  computed: {
    reportTabs () {
      return [
        {
          key: 'daily',
          label: 'My Daily Performance',
          accent: 'var(--brand-gradient)',
          items: this.dailyBreakdown,
          emptyText: 'No activity recorded for the selected date range.'
        },
        {
          key: 'item',
          label: 'My Itemwise Sales',
          accent: 'linear-gradient(180deg, #0ea5e9, #0369a1)',
          items: this.itemwiseReport,
          emptyText: 'No items sold in the selected date range.'
        },
        {
          key: 'orders',
          label: 'My Recent Orders',
          accent: 'linear-gradient(180deg, #2a2f8f, #3949ab)',
          items: this.recentOrders,
          emptyText: 'No orders placed in the selected date range.'
        }
      ]
    },
    activeTab () {
      return this.reportTabs.find((tab) => tab.key === this.activeReport) || this.reportTabs[0]
    },
    tileCards () {
      const knownKeys = TILE_ORDER.filter((key) => Object.prototype.hasOwnProperty.call(this.tiles, key))
      const otherKeys = Object.keys(this.tiles).filter((key) => !knownKeys.includes(key))
      return [...knownKeys, ...otherKeys].map((key) => ({
        key,
        label: TILE_LABELS[key] || this.formatLabel(key),
        value: this.tiles[key]
      }))
    },
    skeletonTileCount () {
      return TILE_ORDER.length
    }
  },
  created () {
    this.fetchDashboardData()
  },
  methods: {
    toDateInputValue (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    getPayload () {
      return {
        fromDate: this.filters.fromDate,
        toDate: this.filters.toDate
      }
    },
    applyFilters () {
      this.fetchDashboardData()
    },
    reloadData () {
      this.fetchDashboardData()
    },
    tileIcon (key) {
      return tileIconFor(key)
    },
    tileCardClass (key, index) {
      const toneMap = {
        no_of_orders_today: 'tile-indigo',
        bottles_sold_today: 'tile-slate',
        sales_amount: 'tile-emerald',
        pending_payment: 'tile-amber',
        return_rate: 'tile-amber',
        returns: 'tile-crimson'
      }
      const fallbackTones = ['tile-indigo', 'tile-ocean', 'tile-emerald', 'tile-amber', 'tile-slate', 'tile-crimson']
      return toneMap[key] || fallbackTones[index % fallbackTones.length]
    },
    async fetchDashboardData () {
      if (!this.filters.fromDate || !this.filters.toDate) {
        this.errorMessage = 'Please select both From Date and To Date.'
        return
      }
      if (this.filters.fromDate > this.filters.toDate) {
        this.errorMessage = 'From Date must be before or equal to To Date.'
        return
      }

      this.isLoading = true
      this.errorMessage = ''

      try {
        const payload = this.getPayload()
        const response = await axios.post(`${this.$apiAdress}/api/dashboard/field_staff/combined`, payload)
        const combined = this.normalizeObject(response.data)

        this.tiles = this.normalizeObject(combined.tiles)
        const data = this.normalizeObject(combined.dashboard)
        this.dailyBreakdown = Array.isArray(data.daily_breakdown) ? data.daily_breakdown : []
        this.itemwiseReport = Array.isArray(data.itemwise_report) ? data.itemwise_report : []
        this.recentOrders = Array.isArray(data.recent_orders) ? data.recent_orders : []
        this.lastUpdatedAt = new Date()
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Unable to load dashboard data right now.'
      } finally {
        this.isLoading = false
      }
    },
    normalizeObject (payload) {
      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        return payload
      }
      return {}
    },
    formatLabel (key) {
      return String(key || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
    },
    formatTileValue (key, value) {
      if (value === null || value === undefined || value === '') return 'N/A'
      if (['sales_amount', 'pending_payment'].includes(key)) {
        const numeric = Number(value)
        if (Number.isNaN(numeric)) return value
        return new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          maximumFractionDigits: 2
        }).format(numeric)
      }
      if (['return_rate'].includes(key)) {
        if (typeof value === 'string' && value.trim().endsWith('%')) return value
        return `${value} %`
      }
      return value
    }
  }
}
</script>

<style scoped>
.dashboard-page {
  border-radius: 14px;
  background: linear-gradient(180deg, #f4f7fb 0%, #eef3fa 100%);
}

.dashboard-card {
  border-radius: 14px;
}

.filter-bar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.tight-row {
  margin-left: -6px;
  margin-right: -6px;
  row-gap: 0.4rem;
}

.tight-row > [class*='col-'],
.tight-row > .col {
  padding-left: 6px;
  padding-right: 6px;
}

.reports-section {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.report-tabs {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
  padding: 0.25rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  width: fit-content;
  max-width: 100%;
  overflow-x: auto;
}

.report-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.1rem;
  border: none;
  outline: none;
  background: transparent;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.18s ease;
  -webkit-tap-highlight-color: transparent;
}

.report-tab:focus { outline: none; box-shadow: none; }
.report-tab:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(14, 91, 70, 0.25); }

.report-tab:hover:not(.is-active) {
  background: #f1f5f9;
  color: #334155;
}

.report-tab.is-active {
  background: var(--brand-gradient);
  color: #ffffff;
  box-shadow: var(--brand-shadow);
}

.tab-count {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
  background: rgba(14, 91, 70, 0.10);
  color: var(--brand-color);
}

.report-tab.is-active .tab-count {
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
}

.filter-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-btn {
  height: 40px;
  min-width: 110px;
  padding: 0 1.1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  border-radius: 999px;
}

.reload-icon-btn {
  min-width: 40px;
  width: 40px;
  padding: 0;
  background: #ffffff;
}

.reload-glyph {
  font-size: 1.1rem;
  line-height: 1;
  font-weight: 700;
  color: #475569;
}

.tile-card {
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  color: #ffffff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.10);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.tile-card::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.18), rgba(255, 255, 255, 0) 55%);
  pointer-events: none;
}

.tile-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.18);
}

.tile-body {
  padding: 1.2rem 1.25rem;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  height: 100%;
}

.tile-text {
  min-width: 0;
  flex: 1 1 auto;
}

.tile-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  color: #ffffff;
  flex: 0 0 auto;
}

.tile-icon .c-icon {
  width: 20px;
  height: 20px;
  color: #ffffff;
}

.tile-value {
  font-size: 1.3rem;
  font-weight: 800;
  line-height: 1.1;
  color: #ffffff;
  letter-spacing: -0.01em;
  margin-bottom: 0.35rem;
}

.tile-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.01em;
}

.tile-slate    { background: linear-gradient(135deg, #1f2a44 0%, #34405e 100%); }
.tile-indigo   { background: linear-gradient(135deg, #2a2f8f 0%, #3949ab 100%); }
.tile-ocean    { background: linear-gradient(135deg, #2f80c8 0%, #4ea3e0 100%); }
.tile-emerald  { background: linear-gradient(135deg, #2e9d5f 0%, #43b97a 100%); }
.tile-amber    { background: linear-gradient(135deg, #e58e1a 0%, #f1ab3c 100%); }
.tile-crimson  { background: linear-gradient(135deg, #c0392b 0%, #e15543 100%); }

.tile-skeleton {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  background: linear-gradient(135deg, #eef1f7 0%, #f7f9fc 100%);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.06);
  position: relative;
  overflow: hidden;
}

.tile-skeleton::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    100deg,
    transparent 30%,
    rgba(255, 255, 255, 0.72) 50%,
    transparent 70%
  );
  transform: translateX(-100%);
  animation: tile-shimmer 1.6s ease-in-out infinite;
}

.tile-skeleton__body {
  padding: 1.2rem 1.25rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.55rem;
}

.tile-skeleton__lines {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.tile-skeleton__line {
  height: 14px;
  border-radius: 6px;
  background: rgba(15, 23, 42, 0.08);
}

.tile-skeleton__line--value { width: 55%; height: 18px; }
.tile-skeleton__line--label { width: 78%; height: 10px; }

.tiles-row > [class*='col-']:nth-child(2)  .tile-skeleton::after { animation-delay: 0.08s; }
.tiles-row > [class*='col-']:nth-child(3)  .tile-skeleton::after { animation-delay: 0.16s; }
.tiles-row > [class*='col-']:nth-child(4)  .tile-skeleton::after { animation-delay: 0.24s; }
.tiles-row > [class*='col-']:nth-child(5)  .tile-skeleton::after { animation-delay: 0.32s; }
.tiles-row > [class*='col-']:nth-child(6)  .tile-skeleton::after { animation-delay: 0.40s; }

@keyframes tile-shimmer {
  0%   { transform: translateX(-100%); }
  60%  { transform: translateX(100%); }
  100% { transform: translateX(100%); }
}

@media (max-width: 767px) {
  .filter-bar { flex-direction: column; align-items: stretch; }
  .filter-actions { width: 100%; }
  .action-btn { flex: 1; }
  .report-tabs {
    width: 100%;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .report-tabs::-webkit-scrollbar { display: none; }
  .report-tab {
    padding: 0.45rem 0.85rem;
    font-size: 0.8rem;
    flex: 0 0 auto;
  }
  .tab-count { font-size: 0.65rem; padding: 0.1rem 0.4rem; }
}
</style>
